import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Truck, Star, Shield } from 'lucide-react';
import { getProducts, getCategories } from '../../api/products';
import ProductCard from '../../components/customer/ProductCard';
import SubscribeForm from '../../components/customer/SubscribeForm';
import styles from './HomePage.module.css';

export default function HomePage() {
  const { t } = useTranslation();

  const { data: featuredData } = useQuery({
    queryKey: ['products', 'featured'],
    queryFn: () => getProducts({ featured: 'true', limit: '8' }),
  });

  const { data: catData } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  });

  const features = [
    { icon: Truck, title: t('home.free_shipping'), desc: t('home.free_shipping_desc') },
    { icon: Star, title: t('home.handpicked'), desc: t('home.handpicked_desc') },
    { icon: Shield, title: t('home.secure_payment'), desc: t('home.secure_payment_desc') },
  ];

  return (
    <div className={styles.page}>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <span className={styles.heroBadge}>Est. 1984 · Chennai</span>
          <h1 className={styles.heroTitle}>{t('home.hero_title')}</h1>
          <p className={styles.heroSub}>{t('home.hero_subtitle')}</p>
          <Link to="/catalogue" className={styles.heroCta}>{t('home.shop_now')}</Link>
        </div>
        <div className={styles.heroDecor}>
          <div className={styles.heroPattern} />
        </div>
      </section>

      {/* Feature strip */}
      <section className={styles.features}>
        {features.map(({ icon: Icon, title, desc }) => (
          <div key={title} className={styles.featureItem}>
            <Icon size={28} strokeWidth={1.5} color="var(--color-gold)" />
            <div>
              <div className={styles.featureTitle}>{title}</div>
              <div className={styles.featureDesc}>{desc}</div>
            </div>
          </div>
        ))}
      </section>

      {/* Categories */}
      {catData?.categories?.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('home.categories')}</h2>
          <div className={styles.categories}>
            {catData.categories.map((cat: { id: string; name: string; nameTA?: string }) => (
              <Link
                key={cat.id}
                to={`/catalogue?category=${cat.id}`}
                className={styles.categoryCard}
              >
                <span>{cat.name}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured products */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('home.featured')}</h2>
        <div className={styles.productGrid}>
          {(featuredData?.items || []).map((product: Record<string, unknown>) => (
            <ProductCard key={product.id as string} product={product} />
          ))}
          {!featuredData && Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={styles.skeleton} />
          ))}
        </div>
        <div className={styles.viewAll}>
          <Link to="/catalogue" className={styles.viewAllBtn}>View All Sarees →</Link>
        </div>
      </section>

      {/* About */}
      <section className={styles.about}>
        <div className={styles.aboutInner}>
          <div className={styles.aboutDecor}>
            <div className={styles.aboutDecorInner} />
          </div>
          <div className={styles.aboutContent}>
            <span className={styles.aboutBadge}>Our Story</span>
            <h2 className={styles.aboutTitle}>{t('home.about_title')}</h2>
            <p className={styles.aboutText}>{t('home.about_text')}</p>
            <Link to="/catalogue" className={styles.heroCta}>{t('home.shop_now')}</Link>
          </div>
        </div>
      </section>

      {/* Subscribe */}
      <section className={styles.subscribe}>
        <SubscribeForm />
      </section>
    </div>
  );
}
